/* ============================================================
   Craftier PDP — all component logic

   WHY THIS FILE EXISTS
   global.js updateCustomLiquid() runs on every variant change:

     destinations[i].innerHTML = sources[i].innerHTML;

   It replaces the innerHTML of every .custom__liquid-{section}
   element with a freshly fetched copy. Script tags inserted via
   innerHTML NEVER execute, so any <script> living inside a Custom
   Liquid block dies the first time someone switches a variant.

   So: no logic inside those blocks. Markup and JSON data islands
   only. Everything below runs from here and re-initialises on the
   theme's own events, and click handling is delegated from
   document so it survives nodes being swapped underneath it.

   Config comes from window.CRAFTIER (snippets/craftier-config).
   ============================================================ */

(function () {
  'use strict';

  var CFG = function () { return window.CRAFTIER || {}; };

  /* ---------- helpers ---------- */

  function titled(s) {
    return String(s || '').replace(/\w\S*/g, function (t) {
      return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
    });
  }

  /* Marks a node so we don't bind twice. Replaced nodes arrive
     without the flag, so re-binding after a swap is automatic. */
  function claim(node, key) {
    if (!node || node.dataset[key]) return false;
    node.dataset[key] = '1';
    return true;
  }

  /* ============================================================
     1. SPEC STRIP
     ============================================================ */

  function renderSpec() {
    var roots = document.querySelectorAll('[data-cr-spec]');

    Array.prototype.forEach.call(roots, function (root) {
      var dataEl = document.getElementById(root.dataset.crSpec);
      if (!dataEl) { root.hidden = true; return; }

      var data;
      try { data = JSON.parse(dataEl.textContent); } catch (e) { root.hidden = true; return; }

      var section = root.closest('.shopify-section') || document;
      var variant = currentVariant(section, data);
      var cfg = variant && variant.config;
      if (!cfg) { root.hidden = true; return; }

      /* metal side */
      var colour = colourFromOptions(variant, data);
      var metalLabel = colour || titled(cfg.metal_type) || '';
      var metalBits = [];
      if (cfg.purity) metalBits.push(cfg.purity);
      if (cfg.metal_weight) metalBits.push(Number(cfg.metal_weight).toFixed(2) + ' g');

      if (!metalLabel && !metalBits.length) { root.hidden = true; return; }

      root.querySelector('[data-cr-metal-label]').textContent = metalLabel;
      root.querySelector('[data-cr-metal-value]').innerHTML = spans(metalBits);

      /* diamond side */
      var d = stoneTotals(cfg);
      if (d.found && d.ct > 0) {
        var bits = ['Total ' + d.ct.toFixed(2).replace(/\.?0+$/, '') + ' Ct'];
        if (d.pcs) bits.push(d.pcs + (d.pcs === 1 ? ' pc' : ' pcs'));
        root.querySelector('[data-cr-stone-label]').textContent = 'Lab Grown Diamond';
        root.querySelector('[data-cr-stone-value]').innerHTML = spans(bits);
        root.classList.remove('cr-spec--solo');
      } else {
        root.classList.add('cr-spec--solo');
      }

      root.hidden = false;
    });
  }

  function spans(list) {
    return list.map(function (b) { return '<span>' + b + '</span>'; }).join('');
  }

  function colourFromOptions(variant, data) {
    var names = data.optionNames || [];
    for (var i = 0; i < names.length; i++) {
      var n = String(names[i]).toLowerCase();
      if (n.indexOf('colour') > -1 || n.indexOf('color') > -1) return variant.options[i];
    }
    return null;
  }

  function stoneTotals(cfg) {
    var out = { ct: 0, pcs: 0, found: false };
    var stones = cfg && cfg.advanced_stone_config;
    if (!stones || !stones.length) return out;
    stones.forEach(function (s) {
      if (String(s.stone_type || '').toLowerCase() !== 'diamond') return;
      out.found = true;
      out.ct += Number(s.stone_weight) || 0;
      out.pcs += Number(s.stone_quantity) || 0;
    });
    return out;
  }

  /* Mirrors VariantRadios.updateOptions() — reads the checked radio in each
     fieldset — so we stay in sync without depending on global.js internals. */
  function selectedOptions(section) {
    var wrap = section.querySelector('variant-radios, variant-selects');
    if (!wrap) return null;

    var fieldsets = wrap.querySelectorAll('fieldset');
    if (fieldsets.length) {
      return Array.prototype.map.call(fieldsets, function (fs) {
        var c = fs.querySelector('input[type="radio"]:checked');
        return c ? c.value : null;
      });
    }
    return Array.prototype.map.call(wrap.querySelectorAll('select'), function (s) { return s.value; });
  }

  function currentVariant(section, data) {
    var opts = selectedOptions(section);
    if (opts && opts.length) {
      var hit = data.variants.find(function (v) {
        return v.options.every(function (o, i) { return o === opts[i]; });
      });
      if (hit) return hit;
    }
    var qs = new URLSearchParams(window.location.search).get('variant');
    if (qs) {
      var byId = data.variants.find(function (v) { return String(v.id) === qs; });
      if (byId) return byId;
    }
    return data.variants.find(function (v) { return v.config; }) || null;
  }

  /* ============================================================
     2. DELIVERY ESTIMATE
     ============================================================ */

  function renderEta() {
    var boxes = document.querySelectorAll('[data-cr-eta]');
    if (!boxes.length) return;

    var cfg = CFG().deliveryDays || {};
    var min = Number(cfg.min) || 15;
    var max = Number(cfg.max) || 20;

    var fmt;
    try {
      fmt = new Intl.DateTimeFormat('en-IN', { weekday: 'long', day: 'numeric', month: 'long' });
    } catch (e) {
      fmt = { format: function (d) { return d.toDateString(); } };
    }

    function add(days) {
      var d = new Date();
      d.setDate(d.getDate() + days);
      return d;
    }

    Array.prototype.forEach.call(boxes, function (box) {
      var from = box.querySelector('[data-cr-eta-from]');
      var to = box.querySelector('[data-cr-eta-to]');
      if (!from || !to) return;
      from.textContent = fmt.format(add(min));
      to.textContent = fmt.format(add(max));
      box.hidden = false;
    });
  }

  /* ============================================================
     3. STORE SLIDER DOTS
     ============================================================ */

  function initStoreDots() {
    var track = document.querySelector('[data-cr-store-track]');
    var dots = document.querySelector('[data-cr-store-dots]');
    if (!track || !dots || !claim(track, 'crDots')) return;

    var cards = track.children.length;
    if (cards < 2) return;

    dots.innerHTML = '';
    for (var i = 0; i < cards; i++) {
      var d = document.createElement('span');
      d.className = 'cr-stores__dot' + (i === 0 ? ' is-on' : '');
      dots.appendChild(d);
    }

    function sync() {
      var max = track.scrollWidth - track.clientWidth;
      var idx = max <= 0 ? 0 : Math.round((track.scrollLeft / max) * (cards - 1));
      Array.prototype.forEach.call(dots.children, function (dot, n) {
        dot.classList.toggle('is-on', n === idx);
      });
    }

    track.addEventListener('scroll', function () {
      window.requestAnimationFrame(sync);
    }, { passive: true });
  }

  /* ============================================================
     4. BUY NOW TOGGLE + STORE CHIPS
     ============================================================ */

  /* Shopify's dynamic checkout button is what GoKwik claims for one-click
     Buy Now (gokwik.liquid matches on button text). Showing it is a config
     switch so Buy Now and Book an Appointment can coexist if wanted. */
  function applyBuyNowToggle() {
    var show = !!((CFG().booking || {}).showBuyNow);
    Array.prototype.forEach.call(document.querySelectorAll('[data-cr-buynow]'), function (n) {
      n.hidden = !show;
    });
  }

  /* Chips render from window.CRAFTIER.stores so the booking modal and the
     Try in Store strip can never list different branches. */
  function renderStoreChips() {
    var track = document.querySelector('[data-cr-store-track]');
    if (!track || track.dataset.crFilled) return;

    var stores = CFG().stores || [];
    if (!stores.length) return;

    track.innerHTML = '';
    stores.forEach(function (s) {
      var chip = document.createElement('div');
      chip.className = 'cr-store';
      chip.innerHTML = '<span class="cr-store__name"></span><span class="cr-store__meta"></span>';
      chip.querySelector('.cr-store__name').textContent = (s.name || '').replace(/^Craftier\s*[—-]\s*/, '');
      chip.querySelector('.cr-store__meta').textContent = s.area || '';
      track.appendChild(chip);
    });
    track.dataset.crFilled = '1';
  }

  /* ============================================================
     5. STICKY ADD TO CART
     ============================================================ */

  function initSticky() {
    var bar = document.querySelector('[data-cr-sticky]');
    if (!bar) return;

    var sectionId = bar.dataset.crSection;
    var section = document.getElementById('shopify-section-' + sectionId) || document;

    function realAtc() {
      return section.querySelector('product-form .product-form__submit, product-form button[type="submit"]');
    }

    function syncButton() {
      var proxy = bar.querySelector('[data-cr-sticky-atc]');
      var btn = realAtc();
      if (!proxy || !btn) return;
      proxy.disabled = btn.disabled;
      var label = btn.querySelector('span:not(.visually-hidden)');
      var text = (label ? label.textContent : btn.textContent).trim();
      if (text) proxy.textContent = text;
    }

    function syncPrice() {
      var slot = bar.querySelector('[data-cr-sticky-price]');
      var src = section.querySelector('#price-' + sectionId + ' .price, #price-' + sectionId);
      if (!slot || !src) return;

      var now = src.querySelector('.current-price');
      var was = src.querySelector('.compare-price');

      if (!now) return;
      slot.innerHTML = '';
      if (was && was.textContent.trim()) {
        var w = document.createElement('span');
        w.className = 'cr-sticky__was';
        w.textContent = was.textContent.trim();
        slot.appendChild(w);
      }
      var p = document.createElement('span');
      p.className = 'cr-sticky__price';
      p.textContent = now.textContent.trim();
      slot.appendChild(p);
    }

    function syncAll() { syncButton(); syncPrice(); }
    syncAll();

    if (!claim(bar, 'crSticky')) return;

    document.body.classList.add('cr-has-sticky');

    var anchor = section.querySelector('product-form');
    if ('IntersectionObserver' in window && anchor) {
      new IntersectionObserver(function (entries) {
        bar.classList.toggle('is-visible', !entries[0].isIntersecting);
      }, { rootMargin: '0px 0px -40px 0px' }).observe(anchor);
    } else {
      bar.classList.add('is-visible');
    }
  }

  /* ============================================================
     DELEGATED CLICKS — bound once, survive every DOM swap
     ============================================================ */

  document.addEventListener('click', function (e) {
    var t = e.target;

    if (t.closest('[data-cr-sticky-atc]')) {
      e.preventDefault();
      var bar = t.closest('[data-cr-sticky]');
      var section = document.getElementById('shopify-section-' + (bar && bar.dataset.crSection)) || document;
      var btn = section.querySelector('product-form .product-form__submit, product-form button[type="submit"]');
      if (btn && !btn.disabled) btn.click();
      return;
    }
  });

  /* ============================================================
     INIT — idempotent, re-runs after every variant change
     ============================================================ */

  function initAll() {
    renderSpec();
    renderEta();
    applyBuyNowToggle();
    renderStoreChips();
    initStoreDots();
    initSticky();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* The theme fires productInfo:loaded after updateCustomLiquid() has
     swapped our markup out. That is the moment to rebuild. */
  document.addEventListener('productInfo:loaded', initAll);
  document.addEventListener('variant:change', initAll);
  document.addEventListener('shopify:section:load', initAll);

  /* Belt and braces: a change anywhere in the picker re-renders the strip
     immediately, so it updates even if the fetch above is slow. */
  document.addEventListener('change', function (e) {
    if (!e.target.closest('variant-radios, variant-selects')) return;
    window.requestAnimationFrame(renderSpec);
  });
})();
